import React, { useCallback, useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import {
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Box,
  RadioGroup,
  Radio,
  Typography,
  Link,
  Chip,
  Alert,
  Snackbar,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
// VisibilityIcon removed (unused)
import { FaEdit } from 'react-icons/fa';

// Components
import CustomButton from "../../components/common/CustomButton/CustomButton.tsx";
import { Column } from "../../components/common/CustomDataTable/CustomDataTable.tsx";
import { CustomTabPanel, SubHeaderTitle } from "../../components/common/index";
import { CommonData, } from "../PageImports.ts";
import ErrorPage from "../Error/ErrorPage.tsx";
import Loader from "../../utils/Loader.tsx";

// Utilities
import { axiosFunction } from '../../components/commonFunctions/axiosFunction.ts';
import { v2ReactURL } from '../../utils/constant.ts';
import { capitalizeFirstChar, CheckedValue, debounce, ErrorState, Pagination } from '../Contact/MainDataTable.tsx';

const {
  Wrapper,
  WrapperSubHeader,
  WrapperContent,
  WrapperCard,
  Row,
  CustomDataTable,
} = CommonData;

// const { toastMessage } = CommonFunctions;

interface PromotionProps {
  navUrl: string;
}

interface PromotionData {
  active: number;
  createdBy: string;
  startDate: string;
  endDate: string;
  categoryId: number;
  categoryName: string | null;
  promotionId: number;
  bannerName: string;
  imageSrc: string | null;
  ordering: number | null;
  promotionNo: string;
  promotionType?: string;
  startTime?: string;
  endTime?: string;
  createdOn?: string;
  mediaType?: string;
}

interface PromotionFormData {
  bannerName: string;
  promotionType: string;
  categoryId: number;
  active: number;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  file?: File | null;
  orderNo?: any;
  mediaType: string;
}

interface Category {
  categoryId: number;
  categoryName: string;
}

interface UserPermissions {
  canView: boolean;
  canInsert: boolean;
  canEdit: boolean;
  canDelete: boolean;
  loading: boolean;
  error: string | null;
}

const PromotionMainDataTable: React.FC<PromotionProps> = ({ navUrl }) => {
  const tabView = navUrl;

  // Permission state
  const [userPermissions, setUserPermissions] = useState<UserPermissions>({
    canView: false,
    canInsert: false,
    canEdit: false,
    canDelete: false,
    loading: true,
    error: null
  });

  const [selectedPromotion, setSelectedPromotion] = useState<PromotionData | null>(null);
  const [openModal, setOpenModal] = useState<boolean>(false);
  const [openCreateModal, setOpenCreateModal] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [rows, setRows] = useState<PromotionData[]>([]);
  const [loadingData, setLoadingData] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [deleting, setDeleting] = useState<boolean>(false);
  const [pagination, setPagination] = useState<Pagination>({
    page: 0,
    pages: 0,
    perpage: 0,
    total: 0,
  });
  const [pageOption, setPageOption] = useState<string>('15');
  const [searchValue, setSearchValue] = useState<string>('');
  const [error, setError] = useState<ErrorState | null>(null);
  const [checkedValue, setCheckedValue] = useState<CheckedValue>({});
  const [openFilter, setOpenFilter] = useState<string | null>(null);
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  // Form state
  const [formData, setFormData] = useState<PromotionFormData>({
    bannerName: '',
    promotionType: 'general',
    categoryId: 0,
    active: 1,
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    file: null,
    orderNo: '',
    mediaType: 'image',
  });

  const [fileName, setFileName] = useState<string>('');
  const [showValidation, setShowValidation] = useState<boolean>(false);
  const [orderNoError, setOrderNoError] = useState<string>('');
  const [categories, setCategories] = useState<Category[]>([]);
  const [loadingCategories, setLoadingCategories] = useState<boolean>(false);

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'warning' | 'info',
    vertical: 'bottom' as 'top' | 'bottom',
    horizontal: 'right' as 'left' | 'center' | 'right'
  });

  // Show snackbar function
  const showSnackbar = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity,
      vertical: 'bottom',
      horizontal: 'right'
    });
  };

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
  if (reason === 'clickaway') {
    return;
  }
  setSnackbar(prev => ({ ...prev, open: false }));
};

  // Override toastMessage function to use our custom snackbar
  const customToastMessage = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'success') => {
    showSnackbar(message, type);
  };

  // Get today's date in YYYY-MM-DD format for disabling previous dates
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0] || '';
  };

  // Enhanced time conversion function
  const convertTimeTo24Hour = (timeString: string): string => {
    if (!timeString?.trim()) return '00:00';

    if (timeString.includes(':') && !timeString.includes(' ') && !timeString.toLowerCase().includes('am') && !timeString.toLowerCase().includes('pm')) {
      return timeString.length === 5 ? timeString : timeString.padStart(5, '0');
    }

    const [time = '', modifier = ''] = timeString.split(' ');
    const [hours = '00', minutes = '00'] = time.split(':');

    let finalHours = parseInt(hours, 10);

    if (modifier.toUpperCase() === 'PM' && finalHours !== 12) {
      finalHours += 12;
    } else if (modifier.toUpperCase() === 'AM' && finalHours === 12) {
      finalHours = 0;
    }

    return `${finalHours.toString().padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  };

  // Enhanced date conversion function
  const convertDateToInputFormat = (dateString: string): string => {
    if (!dateString) return '';

    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [day = '', month = '', year = ''] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) {
        const [month = '', day = '', year = ''] = parts;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
    }

    return dateString;
  };

  // Format date for display in table
  const formatDisplayDate = (dateString: string): string => {
    if (!dateString) return 'N/A';

    if (dateString.includes('/')) {
      return dateString;
    }

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    } catch {
      return dateString;
    }
  };

  // Fetch user permissions
  const fetchUserPermissions = async (): Promise<boolean> => {
    try {
      setUserPermissions(prev => ({ ...prev, loading: true, error: null }));

      const response = await axiosFunction(
        `${v2ReactURL}/native/getuserrolepermission`,
        'GET',
        false
      );

      if (response.data?.status && response.data.response?.reportListResponse) {
        const reportList = response.data.response.reportListResponse;

        if (Array.isArray(reportList)) {
          const promotionPermissions = reportList.find((item: any) =>
            item.type === 'promotion'
          );

          if (promotionPermissions && promotionPermissions.permissionids) {
            let permissionArray: string[] = [];

            if (typeof promotionPermissions.permissionids === 'string') {
              permissionArray = promotionPermissions.permissionids.split(',').map((id: string) => id.trim());
            } else if (Array.isArray(promotionPermissions.permissionids)) {
              permissionArray = promotionPermissions.permissionids.map((id: any) => id.toString());
            }

            const canView = permissionArray.includes('2');

            setUserPermissions({
              loading: false,
              canView: canView,
              canInsert: permissionArray.includes('1'),
              canEdit: permissionArray.includes('3'),
              canDelete: permissionArray.includes('4'),
              error: null
            });

            return canView;
          } else {
            setUserPermissions({
              loading: false,
              canView: false,
              canInsert: false,
              canEdit: false,
              canDelete: false,
              error: 'No promotion permissions found in the response'
            });
            return false;
          }
        } else {
          setUserPermissions({
            loading: false,
            canView: false,
            canInsert: false,
            canEdit: false,
            canDelete: false,
            error: 'Invalid response format: reportListResponse is not an array'
          });
          return false;
        }
      } else {
        setUserPermissions({
          loading: false,
          canView: false,
          canInsert: false,
          canEdit: false,
          canDelete: false,
          error: response.data?.message || 'Failed to load permissions - invalid response structure'
        });
        return false;
      }
    } catch (error: any) {
      console.error('Error fetching permissions:', error);
      setUserPermissions({
        loading: false,
        canView: false,
        canInsert: false,
        canEdit: false,
        canDelete: false,
        error: 'Error fetching permissions from server'
      });
      return false;
    }
  };

  // Fetch categories from API
  const fetchCategories = async () => {
    setLoadingCategories(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/category/categorybrandList`,
        "POST",
        { type: "category/json" },
        false
      );
      if (res.data.status) {
        setCategories(res?.data?.response || []);
      } else if (res.response && Array.isArray(res.response.data)) {
        setCategories(res.response.data);
      } else {
        setCategories([]);
      }
    } catch (error: any) {
      console.error("Error fetching categories:", error);
      customToastMessage("Error fetching categories", 'error');
      setCategories([]);
    } finally {
      setLoadingCategories(false);
    }
  };

  // Check order number uniqueness
  const checkOrderNumber = async (orderNo: string, promotionId: number = 0): Promise<boolean> => {
    try {
      const payload = {
        ordering: parseInt(orderNo),
        promotionId: promotionId
      };

      const res = await axiosFunction(
        `${v2ReactURL}/native/promotion/checkOrdering`,
        "POST",
        payload,
        false
      );

      if (!res.data.response) {
        setOrderNoError("This order number is already taken");
        customToastMessage("This order number is already taken", 'error');
        return false;
      } else {
        setOrderNoError("");
        return true;
      }
    } catch (error: any) {
      console.error("Error checking order number:", error);
      setOrderNoError("");
      return true;
    }
  };

  const fetchData = async (
    searchValue: string,
    startIndex: number,
    pageLength: number,
  ): Promise<void> => {
    setLoadingData(true);
    const payloadData = {
      searchValue: searchValue,
      length: pageLength,
      start: startIndex,
      draw: 1,
    };

    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/promotion/datatable`,
        "POST",
        payloadData
      );

      const responseData = res?.response?.data || res?.data || res;

      if (responseData && responseData.response) {
        const promotionData = responseData.response;

        if (promotionData.data && Array.isArray(promotionData.data)) {
          const promotionList = promotionData.data.map((col: PromotionData, index: number) => ({
            ...col,
            index: index + 1,
            checked: false,
          }));

          setPagination(promotionData.dataTableMetaDTO || {
            page: 0,
            pages: 1,
            perpage: pageLength,
            total: promotionData.data.length
          });
          setRows(promotionList);
        }
      } else if (responseData && Array.isArray(responseData.data)) {
        const promotionList = responseData.data.map((col: PromotionData, index: number) => ({
          ...col,
          index: index + 1,
          checked: false,
        }));

        setPagination(responseData.dataTableMetaDTO || {
          page: 0,
          pages: 1,
          perpage: pageLength,
          total: responseData.data.length
        });
        setRows(promotionList);
      } else {
        throw new Error("Invalid API response format");
      }

      setIsLoading(false);
      setLoadingData(false);

    } catch (error: any) {
      console.error("Error fetching promotion data:", error);
      handleApiError(error);
    }
  };

  const handleApiError = (error: any) => {
    if (error.code === "ERR_NETWORK" || error.message === "network error") {
      setError({
        code: "502",
        message: "Hang tight! Our checkout lanes are undergoing maintenance. We'll be back to serving you real soon!",
      });
    } else if (error.response?.status === 400) {
      setError({
        code: "400",
        message: "Oops, your request got lost in translation! Maybe try again with simpler instructions?",
      });
    } else if (error.response?.status === 404) {
      setError({
        code: "404",
        message: "Oopsie! Looks like this page went on vacation. Maybe try a different path?",
      });
    } else if (error.response?.status === 500) {
      setError({
        code: "500",
        message: "Uh-oh, our server's taking a coffee break. It'll be back to ringing up sales soon!",
      });
    } else {
      setError({
        code: "500",
        message: "An unexpected error occurred while fetching promotion data.",
      });
    }

    customToastMessage("Error fetching promotion details", 'error');
    setLoadingData(false);
    setIsLoading(false);
  };

  const handleSavePromotion = async () => {
    setShowValidation(true);
    const startDateTime = new Date(`${formData.startDate}T${formData.startTime}`);
    const endDateTime = new Date(`${formData.endDate}T${formData.endTime}`);
    if (endDateTime <= startDateTime) {
      customToastMessage("End date and time must be after start date and time", 'error');
      return;
    }

    const isOrderNoAvailable = await checkOrderNumber(
      formData.orderNo,
      selectedPromotion ? selectedPromotion.promotionId : 0
    );

    if (!isOrderNoAvailable) {
      return;
    }

    setSaving(true);
    try {
      const formDataToSend = new FormData();

      formDataToSend.append('bannerName', formData.bannerName.trim());
      formDataToSend.append('ordering', formData.orderNo.toString());
      formDataToSend.append('promotionType', formData.promotionType);
      formDataToSend.append('categoryId', formData.categoryId.toString());
      formDataToSend.append('active', formData.active.toString());
      formDataToSend.append('startDate', formData.startDate);
      formDataToSend.append('endDate', formData.endDate);
      formDataToSend.append('startTime', formData.startTime || '00:00');
      formDataToSend.append('endTime', formData.endTime || '23:59');
      formDataToSend.append('type', formData.mediaType);

      if (selectedPromotion) {
        formDataToSend.append('promotionId', selectedPromotion.promotionId.toString());
      }

      if (formData.file) {
        formDataToSend.append('file', formData.file);
      }

      const res = await axiosFunction(
        `${v2ReactURL}/native/savePramotion`,
        "POST",
        formDataToSend,
        true
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage(`Promotion ${selectedPromotion ? 'updated' : 'created'} successfully!`, 'success');
        setOpenCreateModal(false);
        resetForm();
        handleFetchData();
      } else {
        customToastMessage(res.data?.message || `Failed to ${selectedPromotion ? 'update' : 'create'} promotion`, 'error');
      }
    } catch (error: any) {
      console.error("Error saving promotion:", error);
      customToastMessage(`Error ${selectedPromotion ? 'updating' : 'creating'} promotion details`, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePromotion = async (promotionId: number) => {
    setDeleting(true);
    try {
      const res = await axiosFunction(
        `${v2ReactURL}/native/promotion/${promotionId}/delete`,
        "POST",
        {},
        false
      );

      if (res.status === 200 || res.status === 201) {
        customToastMessage('Promotion deleted successfully!', 'success');
        handleFetchData();
      } else {
        customToastMessage(res.data?.message || 'Failed to delete promotion', 'error');
      }
    } catch (error: any) {
      console.error("Error deleting promotion:", error);
      customToastMessage('Error deleting promotion', 'error');
    } finally {
      setDeleting(false);
    }
  };

  const handleEditPromotion = (row: PromotionData) => {
    setSelectedPromotion(row);

    setFormData({
      bannerName: row.bannerName,
      promotionType: row.promotionType || 'general',
      categoryId: row.categoryId || 0,
      active: row.active,
      startDate: convertDateToInputFormat(row.startDate),
      endDate: convertDateToInputFormat(row.endDate),
      startTime: convertTimeTo24Hour(row.startTime || '00:00'),
      endTime: convertTimeTo24Hour(row.endTime || '23:59'),
      file: null,
      orderNo: row.ordering || '',
      mediaType: row.mediaType || 'image',
    });
    setFileName(row.imageSrc ? 'Existing Image' : '');
    setShowValidation(false);
    setOrderNoError('');
    setOpenCreateModal(true);
  };

  const handleViewPromotion = (row: PromotionData) => {
    setSelectedPromotion(row);
    setOpenModal(true);
  };

  const resetForm = () => {
    setFormData({
      bannerName: '',
      promotionType: 'general',
      categoryId: 0,
      active: 1,
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      file: null,
      orderNo: '',
      mediaType: 'image',
    });
    setFileName('');
    setSelectedPromotion(null);
    setShowValidation(false);
    setOrderNoError('');
  };

  const handleFormChange = (field: keyof PromotionFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'orderNo') {
      setOrderNoError('');
    }

    if (field === 'mediaType') {
      setFileName('');
      setFormData(prev => ({
        ...prev,
        file: null
      }));
    }
  };

  const handleOrderNoBlur = async () => {
    if (formData.orderNo && formData.orderNo !== '') {
      await checkOrderNumber(
        formData.orderNo,
        selectedPromotion ? selectedPromotion.promotionId : 0
      );
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    if (!file) return;

    const maxSize = formData.mediaType === 'image' ? 5 * 1024 * 1024 : 50 * 1024 * 1024;
    if (file.size > maxSize) {
      customToastMessage(`File size must be less than ${formData.mediaType === 'image' ? '5MB' : '50MB'}`, 'error');
      return;
    }

    if (formData.mediaType === 'image') {
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!allowedImageTypes.includes(file.type)) {
        customToastMessage("Please select a valid image file (JPEG, JPG, PNG, GIF)", 'error');
        return;
      }
    } else if (formData.mediaType === 'video') {
      const allowedVideoTypes = ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv'];
      if (!allowedVideoTypes.includes(file.type)) {
        customToastMessage("Please select a valid video file (MP4, AVI, MOV, WMV, FLV)", 'error');
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      file
    }));
    setFileName(file ? file.name : '');
  };

  const handleFetchData = async (): Promise<void> => {
    await fetchData(
      searchValue,
      pagination.page * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  useEffect(() => {
    const initializeData = async () => {
      const hasViewPermission = await fetchUserPermissions();

      if (hasViewPermission) {
        await handleFetchData();
        await fetchCategories();
      } else {
        setIsLoading(false);
      }
    };

    initializeData();
  }, []);

  const handleChangePageOption = (e: { target: { value: any } }) => {
    const selectedValue = e.target.value;
    setPageOption(parseInt(selectedValue).toString());
    fetchData(
      searchValue,
      0,
      parseInt(selectedValue),
    );
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchData(
      searchValue,
      (page - 1) * parseInt(pageOption),
      parseInt(pageOption),
    );
  };

  const handleSearchData = useCallback(
    debounce((searchValue: string) => {
      fetchData(
        searchValue,
        0,
        parseInt(pageOption),
      );
    }, 500),
    [pageOption]
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    handleSearchData(value);
  };

  const columnsDataTablePromotion: Column[] = [
    {
      field: "srNo",
      headerName: "Sr. No",
      width: 80,
      defaultChecked: true,
      renderCell: (params: any) => (
        <Typography variant="body2" color="textSecondary" sx={{ fontSize: '14px' }}>
          {pagination.page * pagination.perpage + params.row.index}
        </Typography>
      ),
    },
    {
      field: "bannerName",
      headerName: "Banner Image",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <img
          src={params.row.imageSrc}
          alt={params.row.bannerName}
          style={{
            width: "50px",
            height: "20px",
            objectFit: "cover",
            borderRadius: "4px",
            cursor: "pointer",
          }}
        />
      ),
    },
    {
      field: "bannerName",
      headerName: "Banner Name",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <Link
          component="button"
          underline="hover"
          sx={{
            color: "#1976d2",
            fontWeight: 500,
            fontSize: "11px",
            cursor: "pointer",
            textDecoration: "underline",
            "&:hover": { textDecoration: "none" },
          }}
          onClick={() => handleViewPromotion(params.row)}
        >
          {params.row.bannerName}
        </Link>
      ),
    },
    {
      field: "mediaType",
      headerName: "Media Type",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip
          label={params.row.mediaType === 'video' ? 'Video' : 'Image'}
          size="small"
          color={params.row.mediaType === 'video' ? 'primary' : 'secondary'}
          variant="outlined"
        />
      ),
    },
    {
      field: "startDate",
      headerName: "Start Date",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" sx={{ fontSize: '10px' }}>
            {formatDisplayDate(params.row.startDate)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "endDate",
      headerName: "End Date",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" sx={{ fontSize: '10px' }}>
            {formatDisplayDate(params.row.endDate)}
          </Typography>
        </Box>
      ),
    },
    {
      field: "createdBy",
      headerName: "Created By",
      width: 120,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography variant="body2" sx={{ fontSize: '10px' }}>
            {params.row.createdBy}
          </Typography>
        </Box>
      ),
    },
    {
      field: "active",
      headerName: "Status",
      width: 100,
      defaultChecked: true,
      renderCell: (params) => (
        <Chip
          label={params.row.active === 1 ? 'Active' : 'Inactive'}
          size="small"
          color={params.row.active === 1 ? 'success' : 'default'}
          variant={params.row.active === 1 ? 'filled' : 'outlined'}
        />
      ),
    },
    {
      field: "Action",
      headerName: "Actions",
      width: 150,
      defaultChecked: true,
      renderCell: (params) => (
        <Box display="flex" gap={1}>
          {userPermissions.canEdit && (
            <IconButton
              size="small"
              onClick={() => handleEditPromotion(params.row)}
              sx={{
                color: 'warning.main',
                '&:hover': { backgroundColor: 'warning.light', color: 'white' }
              }}
            >
              <FaEdit />
            </IconButton>
          )}
          {userPermissions.canDelete && (
            <IconButton
              size="small"
              onClick={() => handleDeletePromotion(params.row.promotionId)}
              disabled={deleting}
              sx={{
                color: 'error.main',
                '&:hover': { backgroundColor: 'error.light', color: 'white' }
              }}
            >
              {deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedPromotion(null);
  };

  const handleCloseCreateModal = () => {
    setOpenCreateModal(false);
    resetForm();
  };

  const toggleFilter = (column: Column) => {
    setSearchInputValue("");
    if (openFilter === column.field) {
      setOpenFilter(null);
    } else {
      setOpenFilter(column.field);
    }
  };

  const handleInputFocus = (field: string) => {
    setOpenFilter(field);
  };

  const handleApplyFilterBtn = () => {
    // Filter implementation if needed
  };

  // Show permission loading
  if (userPermissions.loading) {
    return <Loader />;
  }

  // Check view permission
  if (!userPermissions.canView) {
    return <ErrorPage error={{ code: '403', message: 'You do not have permission to view promotions.' }} />;
  }

  if (tabView === '') {
    return (
      <ErrorPage
        error={{
          code: '403',
          message: "Whoops, access denied! Seems like you hit a dead end. Mysteries of the web, right?",
        }}
      />
    );
  }

  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>{navUrl}</title>
      </Helmet>
      <Wrapper navUrl={navUrl} customStyle={{ height: "750px" }}>
        <WrapperSubHeader className="mainBreadcrumb">
          <div className="mr-auto">
            <SubHeaderTitle subTitle={capitalizeFirstChar(tabView)} />
          </div>
        </WrapperSubHeader>
        <WrapperContent>
          <Row>
            <WrapperCard isCollapsableAnalytics={false}>
              <CustomTabPanel>
                {/* Analytics section can be added here if needed */}
              </CustomTabPanel>

              {/* Compact Header Section */}
              <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2} sx={{ px: 2, pt: '10px' }}>
                <Box display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    placeholder="Search promotions..."
                    value={searchValue}
                    onChange={handleSearchChange}
                    InputProps={{
                      startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
                    }}
                    sx={{ width: 250 }}
                  />
                  {/* Create button - only show if user has insert permission */}
                  {userPermissions.canInsert && (
                    <CustomButton
                      onClick={() => {
                        resetForm();
                        setOpenCreateModal(true);
                      }}
                      btnName={"Create Promotion"}
                      startIcon={<AddIcon />}
                      variant="contained"
                      color="primary"
                      size="small"
                    />
                  )}
                </Box>
              </Box>

              {/* View Promotion Details Modal */}
              <Dialog
                open={openModal}
                onClose={handleCloseModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    boxShadow: "0px 6px 20px rgba(0,0,0,0.15)",
                    backgroundColor: "#fff",
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "14px",
                    color: "#636161",
                  },
                }}
              >
                <DialogTitle
                  sx={{
                    pb: 1,
                    borderBottom: "1px solid #eee",
                    backgroundColor: "#fafafa",
                    borderTopLeftRadius: 8,
                    borderTopRightRadius: 8,
                    color: "#636161",
                  }}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 600,
                        fontSize: "16px",
                        color: "#636161",
                      }}
                    >
                      Promotion Details
                    </Typography>
                    <IconButton
                      onClick={handleCloseModal}
                      size="small"
                      sx={{
                        backgroundColor: "#f0f0f0",
                        "&:hover": { backgroundColor: "#e0e0e0" },
                        color: "#636161",
                      }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </DialogTitle>
                <DialogContent
                  sx={{
                    p: 3,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                    fontSize: "14px",
                    color: "#636161",
                    maxHeight: "70vh",
                    overflow: "auto",
                  }}
                >
                  {selectedPromotion && (
                    <>
                      {/* Compact Media Section */}
                      <Box sx={{ mb: 3, textAlign: "center" }}>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            mb: 2,
                            color: "#292929",
                          }}
                        >
                          {selectedPromotion.mediaType === 'video' ? 'Promotion Video' : 'Promotion Image'}
                        </Typography>

                        <Box
                          sx={{
                            width: "100%",
                            maxWidth: 300,
                            height: 150,
                            backgroundColor: "#f8f9fa",
                            borderRadius: 2,
                            border: "2px dashed #dee2e6",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            margin: "0 auto",
                            overflow: "hidden",
                          }}
                        >
                          {selectedPromotion.imageSrc ? (
                            selectedPromotion.mediaType === 'video' ? (
                              <video
                                controls
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              >
                                <source src={selectedPromotion.imageSrc} type="video/mp4" />
                                Your browser does not support the video tag.
                              </video>
                            ) : (
                              <img
                                src={selectedPromotion.imageSrc}
                                alt="Promotion Banner"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            )
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#6c757d",
                                fontStyle: "italic",
                              }}
                            >
                              No {selectedPromotion.mediaType === 'video' ? 'video' : 'image'} available
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Compact Details Grid - No Scroll Needed */}
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            fontSize: "16px",
                            mb: 2,
                            color: "#292929",
                          }}
                        >
                          Promotion Details
                        </Typography>

                        <Grid container spacing={2}>
                          {/* Column 1 */}
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  BANNER NAME
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.bannerName}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  MEDIA TYPE
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.mediaType === 'video' ? 'Video' : 'Image'}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  ORDER NUMBER
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.ordering || "N/A"}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  START DATE
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {`${formatDisplayDate(selectedPromotion.startDate)}${selectedPromotion.startTime ? ` at ${selectedPromotion.startTime}` : ""
                                    }`}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  CREATED BY
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.createdBy}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>

                          {/* Column 2 */}
                          <Grid item xs={12} sm={6}>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  PROMOTION TYPE
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.promotionType
                                    ? selectedPromotion.promotionType.charAt(0).toUpperCase() +
                                    selectedPromotion.promotionType.slice(1).toLowerCase()
                                    : "General"}
                                  {selectedPromotion.promotionType === "category" && selectedPromotion.categoryName
                                    ? ` (${selectedPromotion.categoryName})`
                                    : " - (N/A)"}
                                </Typography>

                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px", mb: 0.5, display: "block" }}>
                                  STATUS
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: selectedPromotion.active === 1 ? "#28a745" : "#dc3545",
                                    }}
                                  />
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: selectedPromotion.active === 1 ? "#28a745" : "#dc3545",
                                      fontWeight: 500,
                                      fontSize: "13px"
                                    }}
                                  >
                                    {selectedPromotion.active === 1 ? "Active" : "Inactive"}
                                  </Typography>
                                </Box>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  END DATE
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {`${formatDisplayDate(selectedPromotion.endDate)}${selectedPromotion.endTime ? ` at ${selectedPromotion.endTime}` : ""
                                    }`}
                                </Typography>
                              </Box>

                              <Box>
                                <Typography variant="caption" sx={{ color: "#6c757d", fontWeight: 600, fontSize: "11px" }}>
                                  CREATED ON
                                </Typography>
                                <Typography variant="body2" sx={{ color: "#292929", fontSize: "13px" }}>
                                  {selectedPromotion.createdOn || "—"}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>


                    </>
                  )}
                </DialogContent>
              </Dialog>

              {/* Create/Edit Promotion Modal */}
              <Dialog
                open={openCreateModal}
                onClose={handleCloseCreateModal}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 2,
                    maxWidth: 500,
                  },
                }}
              >
                <DialogTitle sx={{ pb: 1 }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6" fontWeight="600">
                      {selectedPromotion ? 'Edit Promotion' : 'Create Promotion'}
                    </Typography>
                    <IconButton onClick={handleCloseCreateModal} size="small">
                      <CloseIcon />
                    </IconButton>
                  </Box>
                </DialogTitle>

                <DialogContent sx={{ p: 3 }}>
                  <Grid container spacing={2}>
                    {/* Banner Name */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Banner Name *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={formData.bannerName}
                        onChange={(e) => handleFormChange('bannerName', e.target.value)}
                        placeholder="Enter banner name"
                        required
                        error={showValidation && !formData.bannerName.trim()}
                        helperText={showValidation && !formData.bannerName.trim() ? "Banner name is required" : ""}
                      />
                    </Grid>

                    {/* Order Number */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Order Number *
                      </Typography>
                      <TextField
                        fullWidth
                        size="small"
                        type="number"
                        value={formData.orderNo}
                        onChange={(e) => handleFormChange('orderNo', e.target.value)}
                        onBlur={handleOrderNoBlur}
                        placeholder="Enter order number"
                        required
                        error={showValidation && (formData.orderNo === '' || formData.orderNo === null || formData.orderNo === undefined || formData.orderNo < 0) || orderNoError !== ''}
                        helperText={
                          orderNoError ? orderNoError :
                            showValidation ? (
                              formData.orderNo === '' || formData.orderNo === null || formData.orderNo === undefined
                                ? "Order number is required"
                                : formData.orderNo < 0
                                  ? "Order number cannot be negative"
                                  : ""
                            ) : ""
                        }
                        inputProps={{
                          min: 0,
                          onKeyDown: (e) => {
                            if (e.key === '-' || e.key === 'e' || e.key === 'E') {
                              e.preventDefault();
                            }
                          }
                        }}
                      />
                    </Grid>

                    {/* Promotion Type */}
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Promotion Type
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: 'grey.50'
                        }}
                      >
                        <RadioGroup
                          row
                          value={formData.promotionType}
                          onChange={(e) => handleFormChange('promotionType', e.target.value)}
                          sx={{
                            gap: 2,
                            '& .MuiFormControlLabel-root': {
                              margin: 0,
                              '& .MuiRadio-root': {
                                padding: '4px',
                                color: 'primary.main',
                              },
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }
                            }
                          }}
                        >
                          <FormControlLabel
                            value="general"
                            control={<Radio size="small" />}
                            label="General"
                          />
                          <FormControlLabel
                            value="category"
                            control={<Radio size="small" />}
                            label="Category"
                          />
                        </RadioGroup>
                      </Box>
                    </Grid>

                    {/* Media Type */}
              

                    {/* Category Selection */}
                    {formData.promotionType === 'category' && (
                      <Grid item xs={6}>
                        <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                          Category *
                        </Typography>
                        <FormControl
                          fullWidth
                          size="small"
                          error={showValidation && formData.categoryId === 0}
                        >
                          <Select
                            value={formData.categoryId}
                            onChange={(e) => handleFormChange('categoryId', e.target.value)}
                            displayEmpty
                            renderValue={(value) => {
                              if (Number(value) === 0) return (
                                <Typography variant="body2" color="textSecondary">
                                  Select Category
                                </Typography>
                              );
                              const selectedCategory = categories.find(cat => cat.categoryId === value);
                              return (
                                <Typography variant="body2" fontWeight="500">
                                  {selectedCategory ? selectedCategory.categoryName : "Select Category"}
                                </Typography>
                              );
                            }}
                            MenuProps={{
                              PaperProps: {
                                sx: {
                                  marginTop: 0.5,
                                  borderRadius: 2,
                                  boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                                  maxHeight: 250,
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  '& .MuiMenuItem-root': {
                                    padding: '10px 16px',
                                    fontSize: '0.875rem',
                                    margin: '2px 8px',
                                    borderRadius: 1,
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white',
                                    },
                                    '&.Mui-selected': {
                                      backgroundColor: 'primary.main',
                                      color: 'white',
                                      fontWeight: 600,
                                      '&:hover': {
                                        backgroundColor: 'primary.dark',
                                      }
                                    }
                                  }
                                }
                              },
                            }}
                            sx={{
                              backgroundColor: 'white',
                              '& .MuiSelect-select': {
                                padding: '8px 16px',
                                borderRadius: 1,
                                border: '1px solid',
                                borderColor: 'divider',
                              },
                              '&:hover': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                }
                              },
                              '&.Mui-focused': {
                                '& .MuiOutlinedInput-notchedOutline': {
                                  borderColor: 'primary.main',
                                  borderWidth: 2,
                                }
                              }
                            }}
                          >
                            {loadingCategories ? (
                              <MenuItem value={0} disabled>
                                <Box display="flex" alignItems="center" gap={1}>
                                  <CircularProgress size={16} />
                                  <Typography variant="body2">Loading categories...</Typography>
                                </Box>
                              </MenuItem>
                            ) : categories.length > 0 ? (
                              categories.map((category) => (
                                <MenuItem key={category.categoryId} value={category.categoryId}>
                                  <Typography variant="body2">
                                    {category.categoryName}
                                  </Typography>
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem value={0} disabled>
                                <Typography variant="body2" color="textSecondary">
                                  No categories found
                                </Typography>
                              </MenuItem>
                            )}
                          </Select>
                          {showValidation && formData.categoryId === 0 && (
                            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                              Please select a category
                            </Typography>
                          )}
                        </FormControl>
                      </Grid>
                    )}

                    {/* Date and Time Section */}
                    <Grid item xs={12}>
                      
                      <Grid container spacing={1}>
                        <Grid item xs={6}>
                          <Typography variant="caption" fontWeight="500" gutterBottom>
                            Start Date *
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={formData.startDate}
                            onChange={(e) => handleFormChange('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: getTodayDate()
                            }}
                            error={showValidation && !formData.startDate}
                            helperText={showValidation && !formData.startDate ? "Start date is required" : ""}
                          />
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption" fontWeight="500" gutterBottom>
                            End Date *
                          </Typography>
                          <TextField
                            fullWidth
                            size="small"
                            type="date"
                            value={formData.endDate}
                            onChange={(e) => handleFormChange('endDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            inputProps={{
                              min: formData.startDate || getTodayDate()
                            }}
                            error={showValidation && !formData.endDate}
                            helperText={showValidation && !formData.endDate ? "End date is required" : ""}
                          />
                        </Grid>

                        {/* Date Validation */}
                        {showValidation && formData.startDate && formData.endDate && (
                          <Grid item xs={12}>
                            {new Date(formData.endDate) < new Date(formData.startDate) && (
                              <Typography variant="caption" color="error">
                                End date must be after start date
                              </Typography>
                            )}
                          </Grid>
                        )}
                      </Grid>
                    </Grid>

                         <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        Media Type
                      </Typography>
                      <Box
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 1,
                          backgroundColor: 'grey.50'
                        }}
                      >
                        <RadioGroup
                          row
                          value={formData.mediaType}
                          onChange={(e) => handleFormChange('mediaType', e.target.value)}
                          sx={{
                            gap: 2,
                            '& .MuiFormControlLabel-root': {
                              margin: 0,
                              '& .MuiRadio-root': {
                                padding: '4px',
                                color: 'primary.main',
                              },
                              '& .MuiTypography-root': {
                                fontSize: '0.875rem',
                                fontWeight: 500,
                              }
                            }
                          }}
                        >
                          <FormControlLabel
                            value="image"
                            control={<Radio size="small" />}
                            label="Image"
                          />
                          <FormControlLabel
                            value="video"
                            control={<Radio size="small" />}
                            label="Video"
                          />
                        </RadioGroup>
                      </Box>
                    </Grid>
                         <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Switch
                            size="medium"
                            checked={formData.active === 1}
                            onChange={(e) => handleFormChange('active', e.target.checked ? 1 : 0)}
                            color="primary"
                          />
                        }
                        label={
                          <Typography variant="subtitle2" fontWeight="500">
                            Active
                          </Typography>
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight="600" gutterBottom>
                        {formData.mediaType === 'video' ? 'Video Upload' : 'Image Upload'} {!selectedPromotion && '*'}
                      </Typography>
                      <Box
                        sx={{
                          border: '1px dashed',
                          borderColor: showValidation && !formData.file && !selectedPromotion ? 'error.main' : 'divider',
                          borderRadius: 1,
                          p: 2,
                          textAlign: 'center',
                          backgroundColor: 'grey.50',
                          cursor: 'pointer',
                        }}
                        onClick={() => document.getElementById('media-upload')?.click()}
                      >
                        <input
                          id="media-upload"
                          type="file"
                          accept={formData.mediaType === 'video' ? 'video/*' : 'image/*'}
                          onChange={handleFileChange}
                          style={{ display: 'none' }}
                        />
                        <CloudUploadIcon sx={{
                          fontSize: 32,
                          color: showValidation && !formData.file && !selectedPromotion ? 'error.main' : 'text.secondary',
                          mb: 1
                        }} />
                        <Typography variant="body2" display="block" color={showValidation && !formData.file && !selectedPromotion ? 'error' : 'textSecondary'} fontWeight="500">
                          {selectedPromotion ? `Click to change ${formData.mediaType}` : `Click to upload ${formData.mediaType}`}
                        </Typography>
                        <Typography variant="caption" color={showValidation && !formData.file && !selectedPromotion ? 'error' : 'textSecondary'}>
                          {formData.mediaType === 'video' 
                            ? 'MP4, AVI, MOV, WMV, FLV (Max 50MB)' 
                            : 'PNG, JPG, JPEG, GIF (Max 5MB)'}
                        </Typography>
                        {fileName && (
                          <Chip
                            label={fileName}
                            size="small"
                            onDelete={() => {
                              setFileName('');
                              handleFormChange('file', null);
                            }}
                            sx={{ mt: 1 }}
                          />
                        )}
                        {showValidation && !formData.file && !selectedPromotion && (
                          <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                            {formData.mediaType === 'video' ? 'Video' : 'Image'} is required
                          </Typography>
                        )}
                        {selectedPromotion && !fileName && (
                          <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                            Current {formData.mediaType} will be used
                          </Typography>
                        )}
                      </Box>
                    </Grid>

                    {/* Status */}
               

                    {/* Action Buttons */}
                    <Grid item xs={12}>
                      <Box display="flex" justifyContent="flex-end" gap={1} mt={1}>
                        <Button
                          size="small"
                          onClick={handleCloseCreateModal}
                          disabled={saving}
                          variant="outlined"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={handleSavePromotion}
                          disabled={saving || orderNoError !== ''}
                          startIcon={saving ? <CircularProgress size={16} /> : null}
                        >
                          {saving ? 'Saving...' : (selectedPromotion ? 'Update' : 'Save')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </DialogContent>
              </Dialog>

              {/* Data Table */}
              {loadingData ? (
                <Box display="flex" justifyContent="center" py={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <CustomDataTable
                  rows={rows}
                  columns={columnsDataTablePromotion}
                  paginationInfo={pagination}
                  handlePageChange={handlePageChange}
                  isCheckboxShow={false}
                  FixedTable={true}
                  pageOption={pageOption}
                  handleChangePageOption={handleChangePageOption}
                  loading={loadingData}
                  isBranchFieldExist={false}
                  setCheckedValue={setCheckedValue}
                  checkedValue={checkedValue}
                  handleApplyFilterBtn={handleApplyFilterBtn}
                  toggleFilter={toggleFilter}
                  openFilter={openFilter}
                  setOpenFilter={setOpenFilter}
                  handleInputFocus={handleInputFocus}
                  branchLocationList={[]}
                  filterData={[]}
                  setFilterData={() => { }}
                  handleSearch={() => { }}
                  contactFilteredIds={[]}
                  setContactFilteredIds={() => { }}
                  filteringData={{}}
                  setSearchInputValue={setSearchInputValue}
                  searchInputValue={searchInputValue}
                  linkField={"promotionNo"}
                />
              )}
            </WrapperCard>
          </Row>
        </WrapperContent>
      </Wrapper>

      {/* Snackbar for notifications - BOTTOM RIGHT */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ 
          vertical: snackbar.vertical, 
          horizontal: snackbar.horizontal 
        }}
        sx={{
          '& .MuiAlert-root': {
            fontSize: '14px',
            fontWeight: 500
          }
        }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            width: '100%',
            boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
            '&.MuiAlert-filledSuccess': {
              backgroundColor: '#4caf50',
            },
            '&.MuiAlert-filledError': {
              backgroundColor: '#f44336',
            },
            '&.MuiAlert-filledWarning': {
              backgroundColor: '#ff9800',
            },
            '&.MuiAlert-filledInfo': {
              backgroundColor: '#2196f3',
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

export default PromotionMainDataTable;